/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import * as Sentry from '@sentry/react';
import React from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';

export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  const tracePropagationUrl = import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_URL;
  const tracePropagationTargets = getTracePropagationTargets(tracePropagationUrl);

  Sentry.init({
    dsn: dsn,
    environment: sentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Prevent sending data from localhost
    enabled: sentryEnvironment !== 'localhost',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    // Tracing
    // Only capture 20% of performance traces
    tracesSampleRate: 0.2,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: tracePropagationTargets,
    // Session Replay
    replaysSessionSampleRate: 0.5, // 50% of sessions will be recorded
    replaysOnErrorSampleRate: 1.0, // Record 100% of sessions where an error happens
    // Enable source maps for unhandled errors
    attachStacktrace: true,
    normalizeDepth: 5,
    maxBreadcrumbs: 50,
    // TODO: add sampleRate to control the amount of error events sent to Sentry and add beforeSend to drop events we dont care about, only after testing under Team Plan.
  });
}

function getTracePropagationTargets(tracePropagationUrl: string | undefined) {
  if (!tracePropagationUrl) return [];

  if (tracePropagationUrl === 'localhost') {
    return ['localhost'];
  }

  const safePattern = new RegExp(`^${tracePropagationUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  return [safePattern];
}
